import React, { useRef } from 'react';

interface FileUploadAreaProps {
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
  error?: string;
}

const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  onFileSelect,
  isLoading = false,
  error
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.name.toLowerCase().endsWith('.csv')) {
        setSelectedFile(file);
        onFileSelect(file);
      } else {
        alert('Hanya file CSV yang didukung');
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      onFileSelect(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={isLoading}
        />

        <div className="mb-4">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v24a4 4 0 004 4h24a4 4 0 004-4V20m-8-12v12m0 0l-4-4m4 4l4-4"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <p className="text-lg font-medium text-gray-700 mb-2">
          Drag and drop file CSV di sini
        </p>
        <p className="text-sm text-gray-500 mb-4">atau klik untuk memilih file</p>

        {selectedFile && (
          <div className="mt-4 p-3 bg-white rounded border border-gray-200">
            <p className="text-sm font-medium text-gray-700">
              📄 {selectedFile.name}
            </p>
            <p className="text-xs text-gray-500">
              {(selectedFile.size / 1024).toFixed(2)} KB
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="text-xl">⚠️</div>
            <div>
              <p className="font-semibold text-red-800 mb-1">Error Upload</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
        <p className="text-sm text-blue-800 mb-2">
          <strong>Format CSV:</strong> Kolom harus berisi: nip, date (YYYY-MM-DD atau YYYY/MM/DD), schedule_type
        </p>
        <p className="text-xs text-blue-700">
          ✓ Mendukung delimiter koma (,) dan titik koma (;)
        </p>
        <p className="text-xs text-blue-700 my-1">
          Contoh: <code className="bg-white px-1 rounded">nip;date;schedule_type</code> atau <code className="bg-white px-1 rounded">nip,date,schedule_type</code>
        </p>
        <p className="text-xs text-blue-700">
          ✓ Mendukung Roster format terbaru
        </p>
        <p className="text-xs text-blue-700 my-1">
          Contoh: <br></br><code className="bg-white rounded">
          nip,2026-02-01,2026-02-02,2026-02-03<br></br>
          TTN-01,day,night,night<br></br></code> 
        </p>
      </div>
    </div>
  );
};

export default FileUploadArea;
